import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n, { i18nReady } from './i18n/i18n';
import { App } from './App';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary';
import { UpdatePrompt } from './components/UpdatePrompt';
import { configureAmplify } from './aws-config';
import { isMockAuth, configureMockAuth } from './test-utils/mock-auth';
import { installGlobalErrorReporting, reportQueryFault } from './lib/reportError';
import './index.css';
import './styles/certificate-fonts.css';

// A3/A5 — report uncaught errors + unhandled promise rejections to /client-error.
installGlobalErrorReporting();

// Configure auth: mock mode for local testing, Amplify for production
if (isMockAuth()) {
  configureMockAuth();
} else {
  configureAmplify();
}

const queryClient = new QueryClient({
  // Auto-capture handled faults (form-gap 400s + server 5xx + network) as deduped
  // support tickets — no manual "report" click needed. reportQueryFault filters
  // out expected/auth errors.
  queryCache: new QueryCache({ onError: (error) => reportQueryFault(error) }),
  mutationCache: new MutationCache({ onError: (error) => reportQueryFault(error) }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry auth errors
        if (error instanceof Error && error.message === 'Not authenticated') return false;
        return failureCount < 2;
      }
    }
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

// Wait for non-English translation bundles to load before rendering
// so pages never flash English content
i18nReady.then(() => {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ChunkErrorBoundary>
        <BrowserRouter>
          <I18nextProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <App />
              <UpdatePrompt />
            </QueryClientProvider>
          </I18nextProvider>
        </BrowserRouter>
      </ChunkErrorBoundary>
    </React.StrictMode>
  );
});
